import { useState, useMemo, useCallback } from "react";
import {
  FileText,
  Layers,
  Activity,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Download,
} from "@credcore/core-ui/icons";
import { MetricCard } from "@/components/common/MetricCard";
import { FilterPanel, FilterState } from "@/components/common/FilterPanel";
import { PerformanceTable } from "@/components/tables/PerformanceTable";
import {
  usePerformanceSummaryQuery,
  usePerformanceDetailsQuery,
  useFilterOptionsQuery,
  Role,
  ClassificationType,
  AnalystPerformance,
  ReviewerPerformance,
} from "@/graphql/types/models";
import { cn } from "@/lib/utils";
import { useOrg } from "@/contexts/OrgContext";
import { DEFAULT_PAGE_SIZE, daysAgo } from "@/lib/constants";
import { Button } from "@/components/shadcn-components/Button";
import { useExcelExport } from "@/hooks/useExcelExport";

type RoleTab = "ANALYST" | "REVIEWER";

export function Dashboard() {
  const { orgId } = useOrg();
  const [activeRole, setActiveRole] = useState<RoleTab>("ANALYST");
  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      from: daysAgo(7),
      to: new Date(),
    },
    personIds: [],
    classifications: [],
  });

  const { isExporting, exportToExcel } = useExcelExport({ orgId });

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const dateRangeFilter = {
    from: filters.dateRange.from?.toISOString(),
    to: filters.dateRange.to?.toISOString(),
  };

  const { data: filterOptionsData } = useFilterOptionsQuery({
    variables: { orgId },
  });

  const classificationWeights = useMemo(
    () => filterOptionsData?.filterOptions?.classificationWeights || [],
    [filterOptionsData],
  );

  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = usePerformanceSummaryQuery({
    variables: {
      filter: {
        orgId,
        role: activeRole === "ANALYST" ? Role.ANALYST : Role.REVIEWER,
        dateRange: dateRangeFilter,
        personIds: filters.personIds.length > 0 ? filters.personIds : undefined,
        classifications:
          filters.classifications.length > 0
            ? (filters.classifications as ClassificationType[])
            : undefined,
      },
    },
  });

  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
    fetchMore,
  } = usePerformanceDetailsQuery({
    variables: {
      filter: {
        orgId,
        role: activeRole === "ANALYST" ? Role.ANALYST : Role.REVIEWER,
        dateRange: dateRangeFilter,
        personIds: filters.personIds.length > 0 ? filters.personIds : undefined,
        classifications:
          filters.classifications.length > 0
            ? (filters.classifications as ClassificationType[])
            : undefined,
        minDocuments: filters.minDocuments,
      },
      first: DEFAULT_PAGE_SIZE,
    },
  });

  const hasError = summaryError || detailsError;

  const handleRetry = useCallback(() => {
    refetchSummary();
    refetchDetails();
  }, [refetchSummary, refetchDetails]);

  type PerformanceNode = AnalystPerformance | ReviewerPerformance;

  const summary = summaryData?.performanceSummary;
  const performanceNodes = useMemo(
    () =>
      (detailsData?.performanceDetails?.edges?.map((edge) => edge.node) ||
        []) as PerformanceNode[],
    [detailsData?.performanceDetails?.edges],
  );

  const handleExportExcel = useCallback(() => {
    exportToExcel({
      dateRange: filters.dateRange,
      personIds: filters.personIds,
      classifications: filters.classifications as ClassificationType[],
      minDocuments: filters.minDocuments,
    });
  }, [exportToExcel, filters]);

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          onClick={() => setActiveRole("ANALYST")}
          className={cn(
            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
            activeRole === "ANALYST"
              ? "bg-gray-900 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200",
          )}
        >
          Analysts
        </button>
        <button
          onClick={() => setActiveRole("REVIEWER")}
          className={cn(
            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
            activeRole === "REVIEWER"
              ? "bg-gray-900 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200",
          )}
        >
          Reviewers
        </button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <FilterPanel role={activeRole} onFilterChange={handleFilterChange} />
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportExcel}
          disabled={isExporting || detailsLoading}
        >
          {isExporting ? (
            <>
              <RefreshCw className="mr-1 h-4 w-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="mr-1 h-4 w-4" />
              Export Excel
            </>
          )}
        </Button>
      </div>

      {hasError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">
                Failed to load data
              </p>
              <p className="text-xs text-red-600">
                {summaryError?.message || detailsError?.message}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="mr-1 h-4 w-4" />
              Retry
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          title="Documents"
          value={summary?.totalDocuments || 0}
          format="number"
          icon={<FileText className="h-4 w-4" />}
          loading={summaryLoading}
        />
        <MetricCard
          title="Fields"
          value={summary?.totalFields || 0}
          format="number"
          icon={<Layers className="h-4 w-4" />}
          loading={summaryLoading}
        />
        <MetricCard
          title="Datapoints"
          value={summary?.totalDatapoints || 0}
          format="number"
          icon={<Activity className="h-4 w-4" />}
          loading={summaryLoading}
        />
        <MetricCard
          title="Errors"
          value={summary?.totalErrors || 0}
          format="number"
          icon={<AlertCircle className="h-4 w-4" />}
          loading={summaryLoading}
        />
        <MetricCard
          title="Overall Score"
          value={summary?.performancePercent || 0}
          format="percent"
          icon={<CheckCircle className="h-4 w-4" />}
          loading={summaryLoading}
        />
      </div>

      <div>
        <PerformanceTable
          data={performanceNodes}
          role={activeRole}
          loading={detailsLoading}
          dateRange={filters.dateRange}
          classificationWeights={classificationWeights}
        />

        {detailsData?.performanceDetails?.pageInfo?.hasNextPage && (
          <div className="mt-4 text-center">
            <button
              onClick={() => {
                fetchMore({
                  variables: {
                    after: detailsData.performanceDetails.pageInfo.endCursor,
                  },
                });
              }}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
